import { TextField } from "@mui/material";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { useRecoilValue, useSetRecoilState } from "recoil";
import * as Yup from "yup";
import AdvancedFilter from "../../../component/AdvancedFilter";
import {
  addFilter,
  getFilterValue,
  renderMultiSelect,
} from "../../../utils/FilterUtils";
import {
  ID_FILTER_DEALERS,
  ID_FILTER_LEGAL_ENTITIES,
  ID_FILTER_PRINTOUT_CREATIONDATEFROM,
  ID_FILTER_PRINTOUT_CREATIONDATETO,
  ID_FILTER_PRINTOUT_INTERMEDIARY,
} from "../constants/IntermediariesFilters";
import {
  STATE_PRINTOUT_ACTIVE_FILTERS,
  STATE_REPORT_ACTIVE_FILTERS,
} from "../constants/IntermediariesRecoil";
import { DatePicker } from "@mui/x-date-pickers";
import { GlobalContext } from "../../../../../util/state/GlobalContextProvider";

function ReportsFilter() {
  const { t } = useTranslation("finance", { keyPrefix: "intermediaries" });
  const { t: tg } = useTranslation("translation");
  const {
    globalData: { user },
  } = React.useContext(GlobalContext);

  const legalEntityOptions: any[] = user.legalEntities
    ?.filter((x: any) => x.name.toLowerCase() !== "all")
    .map((x: any) => ({
      value: x.name,
      label: tg(`legalEntities.${x.name}`),
    }));

  const dealerOptions: any[] = user.dealers
    ?.filter((x: any) => x.name.toLowerCase() !== "all")
    .map((x: any) => ({
      value: x.name,
      label: tg(`dealers.${x.name}`),
    }));

  const [dealers, setDealers] = React.useState<any[]>(dealerOptions);

  const setFilters = useSetRecoilState(STATE_PRINTOUT_ACTIVE_FILTERS);
  const filterDefs = useRecoilValue(STATE_REPORT_ACTIVE_FILTERS);

  const schema = Yup.object().shape({
    intermediaryName: Yup.string(),
    creationDateFrom: Yup.date()
      .nullable()
      .test("testFrom", t("checks.invalidDate"), (value, context) => {
        return (
          !value ||
          !context.parent.creationDateTo ||
          value <= context.parent.creationDateTo
        );
      })
      .typeError(t("checks.invalidDate")),
    creationDateTo: Yup.date()
      .nullable()
      .test("testTo", t("checks.invalidDate"), (value, context) => {
        return (
          !value ||
          !context.parent.creationDateFrom ||
          value >= context.parent.creationDateFrom
        );
      })
      .typeError(t("checks.invalidDate")),
    dealers: Yup.array(),
    legalEntities: Yup.array().min(1, tg("validation.selectAtLeastOne")),
  });

  const defaultValues = {
    intermediaryName: "",
    creationDateFrom: null,
    creationDateTo: null,
    legalEntities:
      getFilterValue(ID_FILTER_LEGAL_ENTITIES, filterDefs) ||
      legalEntityOptions.map((x: any) => x.value),
    dealers: getFilterValue(ID_FILTER_DEALERS, filterDefs) || [],
  };

  const renderIntermediaryName = (field: any) => (
    <TextField
      fullWidth
      value={field.value}
      onChange={field.onChange}
      label={t("filters.intermediary")}
    />
  );

  const renderCreationDateFrom = (
    field: any,
    error: any,
    state: any,
    values: any
  ) => (
    <DatePicker
      label={t("filters.creationDateFrom")}
      views={["day"]}
      maxDate={
        values["creationDateTo"] !== null ? values["creationDateTo"] : null
      }
      value={field.value}
      onChange={field.onChange}
      renderInput={(params) => (
        <TextField
          {...params}
          fullWidth
          id={"creationDateFrom"}
          error={error !== undefined}
          helperText={error ? error.message : ""}
        />
      )}
      inputFormat={"L"}
    />
  );

  const renderCreationDateTo = (
    field: any,
    error: any,
    state: any,
    values: any
  ) => (
    <DatePicker
      label={t("filters.creationDateTo")}
      views={["day"]}
      minDate={
        values["creationDateFrom"] !== null ? values["creationDateFrom"] : null
      }
      value={field.value}
      onChange={field.onChange}
      renderInput={(params) => (
        <TextField
          {...params}
          fullWidth
          id={"creationDateTo"}
          error={error !== undefined}
          helperText={error ? error.message : ""}
        />
      )}
      inputFormat={"L"}
    />
  );

  const mainFields = {
    intermediaryName: renderIntermediaryName,
    creationDateFrom: renderCreationDateFrom,
    creationDateTo: renderCreationDateTo,
  };
  const otherFilters = {
    legalEntities: renderMultiSelect(
      tg("shared.legalEntities"),
      legalEntityOptions,
      "legalEntities",
      legalEntityOptions.length <= 1,
      (value: any, setValue: any) => {
        setValue("dealers", []);
        let dealers = user?.dealers
          ?.filter(
            (x: any) =>
              value?.length > 0 &&
              x.legalEntities.some((e: any) => value.includes(e.name)) &&
              x.name.toLowerCase() !== "all"
          )
          .map((x: any) => {
            return {
              label: tg(`dealers.${x.name}`),
              value: x.name,
            };
          });
        setDealers(dealers);
      }
    ),
    dealers: renderMultiSelect(
      tg("shared.dealers"),
      dealers,
      "dealers",
      dealerOptions.length <= 1
    ),
  };
  const submit = (data: any) => {
    addFilter(
      setFilters,
      ID_FILTER_PRINTOUT_INTERMEDIARY,
      data.intermediaryName
    );
    addFilter(
      setFilters,
      ID_FILTER_PRINTOUT_CREATIONDATEFROM,
      data.creationDateFrom
    );
    addFilter(
      setFilters,
      ID_FILTER_PRINTOUT_CREATIONDATETO,
      data.creationDateTo
    );
    addFilter(setFilters, ID_FILTER_LEGAL_ENTITIES, data.legalEntities);
    addFilter(setFilters, ID_FILTER_DEALERS, data.dealers);
  };

  const reset = () => {
    setFilters([]);
  };

  return (
    <AdvancedFilter
      schema={schema}
      mainFields={mainFields}
      fields={otherFilters}
      defaultValues={defaultValues}
      onSubmit={submit}
      onReset={reset}
    />
  );
}

export default ReportsFilter;
